"use client";

import { isRenderedElement } from "@/lib/ui/rendered-elements";

/**
 * Which screen the edge-swipe-back gesture would take you back from, and how.
 *
 * Every back affordance registers itself here (BackButton does it for the ~80
 * screens that use it; anything hand-rolled uses `useSwipeBackTarget`). The
 * gesture is only offered while a registered affordance is actually laid out,
 * and committing it runs that affordance's own handler — the same
 * `goBackTo()` a tap would — so `?from=`, scroll-restore staging and the
 * history budget all behave exactly as they do for the arrow.
 *
 * Registration happens in an effect, which is what makes this safe under
 * Next 16's `<Activity>`: a route that is hidden has its effects destroyed, so
 * its back button unregisters, and the `isRenderedElement` check covers the
 * moment in between (the hidden subtree is `display: none`).
 */
export type SwipeBackMode =
  /** Slide the page away, then navigate. */
  | "navigate"
  /**
   * Spring the page back into place, then run the handler — for back
   * affordances that ask first (an unpaid hold, unsaved changes), so the
   * question appears over the page instead of over an empty screen.
   */
  | "confirm";

export type SwipeBackTarget = {
  getElement: () => Element | null;
  onBack: () => void;
  mode: SwipeBackMode;
  /** Explicit destination; never infer it from raw browser history. */
  getPreviewHref?: () => string | null;
};

const STATE_KEY = "__hiiveSwipeBack";

type RegistryState = { targets: SwipeBackTarget[]; lastCommitAt: number };

declare global {
  interface Window {
    [STATE_KEY]?: RegistryState;
  }
}

// On `window`, like the navigation-history counter: a module evaluated once
// per chunk must still share one list.
function registry(): RegistryState {
  if (typeof window === "undefined") return { targets: [], lastCommitAt: 0 };
  window[STATE_KEY] ??= { targets: [], lastCommitAt: 0 };
  return window[STATE_KEY];
}

export function registerSwipeBackTarget(target: SwipeBackTarget) {
  const { targets } = registry();
  targets.push(target);
  return () => {
    const index = targets.lastIndexOf(target);
    if (index !== -1) targets.splice(index, 1);
  };
}

/** The most recently mounted back affordance that is on screen, if any. */
export function currentSwipeBackTarget(): SwipeBackTarget | null {
  const { targets } = registry();
  for (let index = targets.length - 1; index >= 0; index -= 1) {
    const target = targets[index];
    if (target && isRenderedElement(target.getElement())) return target;
  }
  return null;
}

/**
 * Stamped when a swipe commits. The Android system back gesture shares the
 * same edge; this lets its handler drop a back that arrives right behind a
 * swipe that already navigated, so one gesture can never go back twice.
 */
export function markSwipeBackCommitted() {
  registry().lastCommitAt = Date.now();
}

export function swipeBackCommittedRecently(windowMs = 500) {
  return Date.now() - registry().lastCommitAt < windowMs;
}
